import React, { useState, useCallback, useEffect, useRef } from 'react';
import { canCaptureFrame } from '../utils/canCaptureFrame';
import { openingZoomFor } from '../utils/cropZoom';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  Alert,
  IconButton,
  Slider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  alpha,
} from '@mui/material';
import {
  Close as CloseIcon,
  LinkedCamera as IPCameraIcon,
  CameraAlt as CaptureIcon,
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  ZoomIn as ZoomInIcon,
  RotateRight as RotateIcon,
  Refresh as RefreshIcon,
  FiberManualRecord as LiveIcon,
  Settings as SettingsIcon,
  Check as CheckmarkIcon,
} from '@mui/icons-material';
import Cropper, { Area } from 'react-easy-crop';
import PhotoCaptureModalEnhanced from './PhotoCaptureModalEnhanced';
import { detectFace, FaceDetectionResult } from '../utils/faceDetection';
import { getCroppedImg, createImage, padImageWithBlack, dataUrlToPhotoOriginal } from '../utils/imageUtils';
import { capturedFrameForEditor } from './photoIPCameraHandover';
import api from '../services/api';
import { IPCameraConfig } from '../hooks/useIPCamera';
import { useStreamKeepalive } from '../hooks/useStreamKeepalive';
import { PhotoHelpTooltip } from './PhotoHelpTooltip';
import { PhotoGuidelinesModal } from './PhotoGuidelinesModal';
import { useOperationalSettings } from '../contexts/OperationalSettingsContext';
import { startAdaptiveSnapshotPolling, AdaptiveSnapshotPollerHandle } from '../utils/adaptiveSnapshotPoller';
import type { PhotoResult } from '../types/photo';

const PADDING_RATIO = 0.5;
const ZOOM_FOR_ORIGINAL_FILL = 1 + 2 * PADDING_RATIO; // 2.0
const CROPPER_MAX_ZOOM = 8;

// Re-exported from the one shared definition (src/types/photo.ts).
export type { PhotoResult };

interface CameraCategory {
  id: string;
  name: string;
  display_name: string;
  description?: string;
  icon?: string;
}

interface RegistrationCamera {
  id: string;
  name: string;
  ip_address: string;
  port: number;
  protocol: 'rtsp' | 'http' | 'mjpeg';
  username?: string;
  password?: string;
  rtsp_path?: string;
  snapshot_path?: string;
  status: 'online' | 'offline' | 'unknown';
  categories?: CameraCategory[];
}

interface PhotoIPCameraModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (result: PhotoResult) => void;
  title?: string;
  customerId?: string;
  /** Filter cameras by category name (e.g., 'visitante', 'documento', 'captura_facial') */
  filterCategory?: string;
  cropShape?: 'round' | 'rect';
  aspectRatio?: number;
  darkMode?: boolean;
  /** No face to find: a vehicle, a document. */
  disableFaceDetection?: boolean;
}

// Direct Electron API call for camera capture - avoids hook lifecycle issues
// Supports both RTSP (via ffmpeg) and HTTP (via curl with digest auth)
async function captureViaElectron(config: IPCameraConfig): Promise<string | null> {
  if (typeof window === 'undefined' || !window.electronAPI) {
    console.log('[PhotoIPCameraModal] Not in Electron environment');
    return null;
  }

  try {
    // Only check ffmpeg for RTSP protocol (HTTP uses curl, not ffmpeg)
    if (config.protocol === 'rtsp') {
      console.log('[PhotoIPCameraModal] Checking ffmpeg for RTSP...');
      const ffmpegCheck = await window.electronAPI.checkFfmpeg();
      if (!ffmpegCheck.available) {
        console.log('[PhotoIPCameraModal] ffmpeg not available');
        return null;
      }
    }

    console.log('[PhotoIPCameraModal] Calling captureIPCameraSnapshot with:', JSON.stringify(config));
    const result = await window.electronAPI.captureIPCameraSnapshot({
      protocol: config.protocol,
      ipAddress: config.ipAddress,
      port: config.port,
      username: config.username,
      password: config.password,
      rtspPath: config.rtspPath,
      snapshotPath: config.snapshotPath,
    });

    console.log('[PhotoIPCameraModal] captureIPCameraSnapshot result:', result.success, result.error);
    if (result.success && result.data) {
      return result.data;
    }
    return null;
  } catch (err) {
    console.error('[PhotoIPCameraModal] Error capturing:', err);
    return null;
  }
}

// Legacy alias for RTSP - keeping for compatibility
const captureRtspViaElectron = captureViaElectron;

const PhotoIPCameraModal: React.FC<PhotoIPCameraModalProps> = ({
  open,
  onClose,
  onConfirm,
  title = 'Captura via Câmera IP',
  customerId,
  filterCategory,
  cropShape = 'rect',
  aspectRatio = 4 / 5,
  darkMode = false,
  disableFaceDetection = false,
}) => {
  const { settings } = useOperationalSettings();

  // Check if in Electron - simple boolean, no hook
  const isInElectron = typeof window !== 'undefined' && window.electronAPI !== undefined;

  // IP Camera state
  const [ipCameras, setIPCameras] = useState<RegistrationCamera[]>([]);
  const [selectedIPCamera, setSelectedIPCamera] = useState<string>('');
  const [isLoadingCameras, setIsLoadingCameras] = useState(false);
  const [isCapturing, setIsCapturing] = useState(false);
  const [captureError, setCaptureError] = useState<string | null>(null);

  // Unified camera preview state (works for both HTTP and RTSP)
  const [cameraPreviewUrl, setCameraPreviewUrl] = useState<string | null>(null);
  const [isStreaming, setIsStreaming] = useState(false);
  const [isStreamReconnecting, setIsStreamReconnecting] = useState(false);
  const [reconnectStatus, setReconnectStatus] = useState<string | null>(null);
  const [streamError, setStreamError] = useState<string | null>(null);
  const [actualFps, setActualFps] = useState(0);
  const [streamQuality, setStreamQuality] = useState<'low' | 'medium' | 'high' | 'ultra'>(() => {
    // Load saved quality preference from localStorage
    const saved = localStorage.getItem('rtsp-stream-quality');
    return (saved as 'low' | 'medium' | 'high' | 'ultra') || 'medium';
  });
  const [showQualityMenu, setShowQualityMenu] = useState(false);
  const [bgColor, setBgColor] = useState<'#000000' | '#ffffff'>('#000000');
  const origDataUrlRef = useRef<string | null>(null);
  const httpPollerRef = useRef<AdaptiveSnapshotPollerHandle | null>(null);
  const currentStreamIdRef = useRef<string | null>(null);
  const frameCountRef = useRef(0);
  const lastFpsUpdateRef = useRef(Date.now());
  const lastFrameTimeRef = useRef(0);
  const isCapturingSnapshotRef = useRef(false);

  // HTTP snapshot polling state (Digest auth fallback when ffmpeg MJPEG fails)
  const [httpSnapshotUrl, setHttpSnapshotUrl] = useState<string | null>(null);

  // Image processing state
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [croppedPreview, setCroppedPreview] = useState<string | null>(null);
  const [isDetectingFace, setIsDetectingFace] = useState(false);
  const [faceDetectionResult, setFaceDetectionResult] = useState<FaceDetectionResult | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showGuidelines, setShowGuidelines] = useState(false);

  const onCropComplete = useCallback((_croppedArea: Area, croppedAreaPixels: Area) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  // Build direct HTTP snapshot URL for camera (used for single-frame capture)
  const buildSnapshotUrl = useCallback((camera: RegistrationCamera): string => {
    const scheme = camera.port === 443 ? 'https' : 'http';
    let auth = '';
    if (camera.username) {
      auth = camera.password
        ? `${camera.username}:${camera.password}@`
        : `${camera.username}@`;
    }
    const path = camera.snapshot_path || '/snapshot.jpg';
    const portStr = (scheme === 'http' && camera.port !== 80) || (scheme === 'https' && camera.port !== 443)
      ? `:${camera.port}`
      : '';
    return `${scheme}://${auth}${camera.ip_address}${portStr}${path}`;
  }, []);

  // Build MJPEG stream URL — derives the httppreview path from the snapshot path
  const buildMjpegUrl = useCallback((camera: RegistrationCamera): string => {
    const scheme = camera.port === 443 ? 'https' : 'http';
    let auth = '';
    if (camera.username) {
      const user = encodeURIComponent(camera.username);
      const pass = camera.password ? encodeURIComponent(camera.password) : '';
      auth = pass ? `${user}:${pass}@` : `${user}@`;
    }
    const snapshotPath = camera.snapshot_path || '';
    // Hikvision ISAPI: swap /picture → /httppreview; otherwise use default MJPEG endpoint
    const mjpegPath = snapshotPath.includes('/picture')
      ? snapshotPath.replace('/picture', '/httppreview')
      : '/ISAPI/Streaming/channels/101/httppreview';
    const portStr = (scheme === 'http' && camera.port !== 80) || (scheme === 'https' && camera.port !== 443)
      ? `:${camera.port}`
      : '';
    return `${scheme}://${auth}${camera.ip_address}${portStr}${mjpegPath}`;
  }, []);

  // Snapshot polling fallback — used when ffmpeg MJPEG fails (e.g. Hikvision Digest auth issues).
  // Adaptive interval (150ms floor, widens to 3s on slow/failed fetches) via Electron IPC
  // (which handles Digest auth), matching iOS/Android's polling loop instead of a fixed 2s tick.
  const startSnapshotPolling = useCallback((camera: RegistrationCamera) => {
    const config = {
      protocol: 'http' as const,
      ipAddress: camera.ip_address,
      port: camera.port,
      username: camera.username,
      password: camera.password,
      snapshotPath: camera.snapshot_path || '/ISAPI/Streaming/channels/101/picture',
    };
    const doCapture = async (): Promise<boolean> => {
      if (isCapturingSnapshotRef.current) return false;
      isCapturingSnapshotRef.current = true;
      try {
        const result = await window.electronAPI?.captureIPCameraSnapshot?.(config);
        if (result?.success && result.data) {
          setHttpSnapshotUrl(result.data);
          return true;
        }
        return false;
      } catch {
        return false;
      } finally {
        isCapturingSnapshotRef.current = false;
      }
    };
    httpPollerRef.current?.stop();
    httpPollerRef.current = startAdaptiveSnapshotPolling(doCapture);
  }, []);

  // Start HTTP camera preview.
  // In Electron: snapshot polling via Electron IPC (handles Digest auth).
  // In browser: MJPEG URL via img.src (Basic auth only).
  const startHttpPreview = useCallback((camera: RegistrationCamera) => {
    setStreamError(null);
    if (isInElectron) {
      startSnapshotPolling(camera);
    } else {
      setCameraPreviewUrl(buildMjpegUrl(camera));
      setIsStreaming(true);
    }
  }, [isInElectron, buildMjpegUrl, startSnapshotPolling]);

  // Load IP cameras
  const loadCameras = useCallback(async () => {
    if (!customerId) {
      console.log('[PhotoIPCameraModal] No customerId provided');
      return;
    }

    setIsLoadingCameras(true);
    console.log('[PhotoIPCameraModal] Loading cameras for customer:', customerId, 'category:', filterCategory);
    try {
      // Note: api instance already has /api/v1 as baseURL, so just use relative path
      // Add category filter if provided
      let url = `/registration-cameras?customer_id=${customerId}`;
      if (filterCategory) {
        url += `&category=${filterCategory}`;
      }
      const response = await api.get(url);
      console.log('[PhotoIPCameraModal] API response:', response.data);
      // Backend returns { success: true, cameras: [...] }
      const cameras: RegistrationCamera[] = response.data?.cameras || response.data?.data || [];
      console.log('[PhotoIPCameraModal] Parsed cameras:', cameras);
      setIPCameras(cameras);

      // Auto-select first camera if none selected and cameras are available
      if (cameras.length > 0) {
        setSelectedIPCamera(cameras[0].id);
        // Preview will auto-start via useEffect
      }
    } catch (err: any) {
      console.error('[PhotoIPCameraModal] Failed to load IP cameras:', err);
      console.error('[PhotoIPCameraModal] Error details:', err.response?.data);
      setIPCameras([]);
    } finally {
      setIsLoadingCameras(false);
    }
  }, [customerId, filterCategory]);

  // Stop camera preview (unified for HTTP and RTSP)
  const stopCameraPreview = useCallback(async () => {
    // Stop HTTP refresh
    if (httpPollerRef.current) {
      httpPollerRef.current.stop();
      httpPollerRef.current = null;
    }

    // Stop RTSP stream
    if (currentStreamIdRef.current && isInElectron) {
      console.log('[PhotoIPCameraModal] Stopping RTSP stream:', currentStreamIdRef.current);
      await window.electronAPI?.stopIPCameraStream?.(currentStreamIdRef.current);
      currentStreamIdRef.current = null;
    }

    setCameraPreviewUrl(null);
    setHttpSnapshotUrl(null);
    setIsStreaming(false);
    setActualFps(0);
    frameCountRef.current = 0;
    lastFrameTimeRef.current = 0;
    isCapturingSnapshotRef.current = false;
  }, [isInElectron]);

  // Start RTSP streaming
  const startRtspStream = useCallback(async (camera: RegistrationCamera) => {
    const streamId = `photo-stream-${camera.id}-${Date.now()}`;
    currentStreamIdRef.current = streamId;

    console.log('[PhotoIPCameraModal] Starting RTSP stream:', streamId, 'quality:', streamQuality);
    setIsStreaming(true);
    setStreamError(null);
    frameCountRef.current = 0;
    lastFpsUpdateRef.current = Date.now();

    try {
      const result = await window.electronAPI?.startIPCameraStream?.({
        streamId,
        ipAddress: camera.ip_address.replace(/^(https?:\/\/|rtsp:\/\/)/i, ''),
        port: camera.port,
        username: camera.username,
        password: camera.password,
        rtspPath: camera.rtsp_path?.replace(/[\x00-\x08\x0B-\x0C\x0E-\x1F]/g, '').trim().replace(/\?\?+/g, '?'),
        fps: streamQuality === 'low' ? 15 : streamQuality === 'ultra' ? 30 : 25,
        quality: streamQuality,
      });

      if (!result || !result.success) {
        console.error('[PhotoIPCameraModal] Failed to start stream:', result?.error);
        setIsStreaming(false);
        currentStreamIdRef.current = null;
      }
    } catch (err) {
      console.error('[PhotoIPCameraModal] Error starting stream:', err);
      setIsStreaming(false);
      currentStreamIdRef.current = null;
    }
  }, [streamQuality]);

  // Handle camera selection change
  const handleCameraChange = useCallback(async (cameraId: string) => {
    setSelectedIPCamera(cameraId);
    await stopCameraPreview();

    const camera = ipCameras.find(c => c.id === cameraId);
    if (camera) {
      if (camera.protocol === 'http' || camera.protocol === 'mjpeg') {
        startHttpPreview(camera);
      } else if (camera.protocol === 'rtsp' && isInElectron) {
        startRtspStream(camera);
      }
    }
  }, [ipCameras, startHttpPreview, startRtspStream, stopCameraPreview, isInElectron]);

  // Load cameras when modal opens
  useEffect(() => {
    if (open && customerId) {
      loadCameras();
    }
  }, [open, customerId, loadCameras]);

  // Start RTSP for first camera after initial load (only runs once)
  const initialRtspStartedRef = useRef(false);
  useEffect(() => {
    if (
      !initialRtspStartedRef.current &&
      ipCameras.length > 0 &&
      selectedIPCamera &&
      !imageSrc &&
      isInElectron
    ) {
      const camera = ipCameras.find(c => c.id === selectedIPCamera);
      if (camera?.protocol === 'rtsp' && !isStreaming) {
        initialRtspStartedRef.current = true;
        startRtspStream(camera);
      } else if ((camera?.protocol === 'http' || camera?.protocol === 'mjpeg') && !cameraPreviewUrl) {
        startHttpPreview(camera);
      }
    }
  }, [ipCameras, selectedIPCamera, imageSrc, isInElectron, isStreaming, cameraPreviewUrl, startRtspStream, startHttpPreview]);

  // Reset the flag when modal closes
  useEffect(() => {
    if (!open) {
      initialRtspStartedRef.current = false;
    }
  }, [open]);

  // Auto-retry: if a camera is selected but stream is not active, keep retrying every 5s
  useEffect(() => {
    if (!selectedIPCamera || isStreaming || cameraPreviewUrl || imageSrc) return;
    const camera = ipCameras.find(c => c.id === selectedIPCamera);
    if (!camera) return;
    // Attempt connection immediately
    if (camera.protocol === 'http' || camera.protocol === 'mjpeg') {
      startHttpPreview(camera);
    } else if (camera.protocol === 'rtsp' && isInElectron) {
      startRtspStream(camera);
    }
    // Then keep retrying every 5s if still not connected
    const timer = setInterval(() => {
      if (camera.protocol === 'http' || camera.protocol === 'mjpeg') {
        startHttpPreview(camera);
      } else if (camera.protocol === 'rtsp' && isInElectron) {
        startRtspStream(camera);
      }
    }, 5000);
    return () => clearInterval(timer);
  }, [selectedIPCamera, isStreaming, cameraPreviewUrl, imageSrc, ipCameras, isInElectron, startHttpPreview, startRtspStream]);

  // Reset state when modal closes
  useEffect(() => {
    if (!open) {
      // CRITICAL: Reset focus to prevent keyboard trap on Windows
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }

      // Small delay to ensure focus is properly released
      setTimeout(() => {
        if (document.body) {
          document.body.focus();
        }
      }, 50);

      stopCameraPreview();
      setImageSrc(null);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setRotation(0);
      setCroppedAreaPixels(null);
      setCroppedPreview(null);
      setFaceDetectionResult(null);
      setError(null);
      setCaptureError(null);
      setSelectedIPCamera('');
    }
  }, [open, stopCameraPreview]);

  // Listen for incoming video frames from continuous RTSP stream
  useEffect(() => {
    if (!isInElectron || !window.electronAPI?.onIPCameraFrame || !window.electronAPI?.onIPCameraStreamEnded) {
      return;
    }

    // Listen for video frames
    const removeFrameListener = window.electronAPI.onIPCameraFrame((data) => {
      // Only process frames for our current stream
      if (data.streamId === currentStreamIdRef.current) {
        setCameraPreviewUrl(data.frame);
        lastFrameTimeRef.current = Date.now();

        // Update FPS counter
        frameCountRef.current++;
        const now = Date.now();
        const elapsed = now - lastFpsUpdateRef.current;
        if (elapsed >= 1000) {
          setActualFps(Math.round((frameCountRef.current / elapsed) * 1000));
          frameCountRef.current = 0;
          lastFpsUpdateRef.current = now;
        }
      }
    });

    // Listen for stream ended events
    const removeStreamEndedListener = window.electronAPI.onIPCameraStreamEnded((data) => {
      if (data.streamId === currentStreamIdRef.current) {
        console.log('[PhotoIPCameraModal] Stream ended:', data.streamId);
        setIsStreaming(false);
        setIsStreamReconnecting(false);
        setReconnectStatus(null);
        setStreamError(null);
        currentStreamIdRef.current = null;
      }
    });

    // Listen for reconnection attempts
    const removeReconnectingListener = window.electronAPI.onIPCameraStreamReconnecting?.((data) => {
      if (data.streamId === currentStreamIdRef.current) {
        setIsStreamReconnecting(true);
        setReconnectStatus(`Reconectando... (tentativa ${data.attempt})`);
        setStreamError(null); // Clear previous errors
      }
    });

    // Listen for successful reconnection
    const removeReconnectedListener = window.electronAPI.onIPCameraStreamReconnected?.((data) => {
      if (data.streamId === currentStreamIdRef.current) {
        setIsStreamReconnecting(false);
        setReconnectStatus(null);
        setStreamError(null);
        console.log(`[PhotoIPCameraModal] Stream reconnected after ${data.attemptsTaken} attempts`);
      }
    });

    // Listen for stream errors
    const removeErrorListener = window.electronAPI.onIPCameraStreamError?.((data) => {
      if (data.streamId === currentStreamIdRef.current) {
        setStreamError(data.message);
        console.error('[PhotoIPCameraModal] Stream error:', data.message);
        // Don't stop the stream - let auto-reconnect handle it
      }
    });

    return () => {
      removeFrameListener();
      removeStreamEndedListener();
      removeReconnectingListener?.();
      removeReconnectedListener?.();
      removeErrorListener?.();
    };
  }, [isInElectron]);

  // Keepalive: Automatically send keepalive signals every 3s to prevent backend
  // from killing the stream after 12s of inactivity (KEEPALIVE_TIMEOUT in main.ts)
  useStreamKeepalive(currentStreamIdRef.current);

  // Generate preview when crop changes
  useEffect(() => {
    const generatePreview = async () => {
      if (!imageSrc || !croppedAreaPixels) return;

      try {
        const result = await getCroppedImg(
          imageSrc,
          croppedAreaPixels,
          rotation,
          { width: 150, height: 150 }
        );
        setCroppedPreview(result.base64);
      } catch (err) {
        console.error('Error generating preview:', err);
      }
    };

    const timeoutId = setTimeout(generatePreview, 100);
    return () => clearTimeout(timeoutId);
  }, [imageSrc, croppedAreaPixels, rotation]);

  // Process captured image
  const processImage = useCallback(async (dataUrl: string) => {
    setError(null);
    setIsDetectingFace(false); // Don't auto-detect if disabled
    stopCameraPreview();

    try {
      origDataUrlRef.current = dataUrl;
      const origImg = await createImage(dataUrl);
      const paddedDataUrl = await padImageWithBlack(dataUrl, PADDING_RATIO, bgColor);
      setImageSrc(paddedDataUrl);

      if (settings.autoFaceDetection) {
        setIsDetectingFace(true);
        const detection = await detectFace(origImg);
        setFaceDetectionResult(detection);
        setIsDetectingFace(false);
      } else {
        setFaceDetectionResult(null);
      }

      // Always center regardless of face detection. One rule shared by every capture modal:
      // fill for a face, fit for a vehicle or document. See utils/cropZoom.
      setCrop({ x: 0, y: 0 });
      setZoom(openingZoomFor({
        image: { width: origImg.width, height: origImg.height },
        cropAspect: aspectRatio,
        cropShape,
        baseZoom: ZOOM_FOR_ORIGINAL_FILL,
      }));
      setRotation(0);
    } catch (err) {
      console.error('Error processing image:', err);
      setError('Erro ao processar a imagem. Tente novamente.');
      setIsDetectingFace(false);
    }
  }, [settings.autoFaceDetection, stopCameraPreview, bgColor, aspectRatio, cropShape]);

  // Re-pad image when background color changes
  useEffect(() => {
    if (!origDataUrlRef.current || !imageSrc) return;
    const rePad = async () => {
      const paddedDataUrl = await padImageWithBlack(origDataUrlRef.current!, PADDING_RATIO, bgColor);
      setImageSrc(paddedDataUrl);
      setCrop({ x: 0, y: 0 });
      setZoom(ZOOM_FOR_ORIGINAL_FILL);
    };
    rePad();
  }, [bgColor]); // eslint-disable-line react-hooks/exhaustive-deps

  // Capture from IP camera - direct HTTP request
  const handleCapture = useCallback(async () => {
    if (!selectedIPCamera) {
      setError('Selecione uma câmera primeiro.');
      return;
    }

    const camera = ipCameras.find((c) => c.id === selectedIPCamera);
    if (!camera) {
      setError('Câmera não encontrada.');
      return;
    }

    setCaptureError(null);
    setError(null);
    setIsCapturing(true);

    try {
      if (camera.protocol === 'http' || camera.protocol === 'mjpeg') {
        // For HTTP/ONVIF cameras - use Electron if available (for digest auth support)
        if (isInElectron) {
          console.log('[PhotoIPCameraModal] Using Electron API for HTTP camera (digest auth)');
          const config: IPCameraConfig = {
            protocol: 'http',
            ipAddress: camera.ip_address,
            port: camera.port,
            username: camera.username,
            password: camera.password,
            snapshotPath: camera.snapshot_path || '/ISAPI/Streaming/channels/101/picture',
          };

          const dataUrl = await captureViaElectron(config);
          if (dataUrl) {
            await processImage(dataUrl);
          } else {
            // Fallback to browser fetch if Electron fails
            console.log('[PhotoIPCameraModal] Electron capture failed, trying browser fallback');
            const snapshotUrl = buildSnapshotUrl(camera);
            const img = new Image();
            img.crossOrigin = 'anonymous';

            const loadPromise = new Promise<string>((resolve, reject) => {
              img.onload = () => {
                const canvas = document.createElement('canvas');
                canvas.width = img.naturalWidth;
                canvas.height = img.naturalHeight;
                const ctx = canvas.getContext('2d');
                if (ctx) {
                  ctx.drawImage(img, 0, 0);
                  const dataUrl = canvas.toDataURL('image/jpeg', 0.92);
                  resolve(dataUrl);
                } else {
                  reject(new Error('Failed to get canvas context'));
                }
              };
              img.onerror = () => {
                reject(new Error('Não foi possível conectar à câmera. Verifique se a câmera está online e acessível.'));
              };
              img.src = `${snapshotUrl}${snapshotUrl.includes('?') ? '&' : '?'}t=${Date.now()}`;
            });

            const timeoutPromise = new Promise<string>((_, reject) => {
              setTimeout(() => reject(new Error('Timeout ao conectar com a câmera')), 10000);
            });

            const browserDataUrl = await Promise.race([loadPromise, timeoutPromise]);
            await processImage(browserDataUrl);
          }
        } else {
          // Not in Electron - use browser fetch (won't work for digest auth cameras)
          console.log('[PhotoIPCameraModal] Using browser fetch for HTTP camera');
          const snapshotUrl = buildSnapshotUrl(camera);
          const img = new Image();
          img.crossOrigin = 'anonymous';

          const loadPromise = new Promise<string>((resolve, reject) => {
            img.onload = () => {
              const canvas = document.createElement('canvas');
              canvas.width = img.naturalWidth;
              canvas.height = img.naturalHeight;
              const ctx = canvas.getContext('2d');
              if (ctx) {
                ctx.drawImage(img, 0, 0);
                const dataUrl = canvas.toDataURL('image/jpeg', 0.92);
                resolve(dataUrl);
              } else {
                reject(new Error('Failed to get canvas context'));
              }
            };
            img.onerror = () => {
              reject(new Error('Não foi possível conectar à câmera. Verifique se a câmera está online e acessível.'));
            };
            img.src = `${snapshotUrl}${snapshotUrl.includes('?') ? '&' : '?'}t=${Date.now()}`;
          });

          const timeoutPromise = new Promise<string>((_, reject) => {
            setTimeout(() => reject(new Error('Timeout ao conectar com a câmera')), 10000);
          });

          const dataUrl = await Promise.race([loadPromise, timeoutPromise]);
          await processImage(dataUrl);
        }

      } else if (camera.protocol === 'rtsp') {
        // RTSP cameras - use the current frame from live stream if available
        if (isInElectron) {
          console.log('[PhotoIPCameraModal] Using RTSP frame for capture');

          // If we have a live stream frame, use it directly
          if (cameraPreviewUrl) {
            await processImage(cameraPreviewUrl);
          } else {
            // Otherwise capture a new frame
            const config: IPCameraConfig = {
              protocol: 'rtsp',
              ipAddress: camera.ip_address,
              port: camera.port,
              username: camera.username,
              password: camera.password,
              rtspPath: camera.rtsp_path,
            };

            const dataUrl = await captureRtspViaElectron(config);
            if (dataUrl) {
              await processImage(dataUrl);
            } else {
              setError('Erro ao capturar foto da câmera RTSP. Verifique a conexão com a câmera e se o FFmpeg está instalado.');
            }
          }
        } else {
          // Not in Electron - RTSP cannot be accessed from browser
          console.log('[PhotoIPCameraModal] Not in Electron, showing error');
          setError('Câmeras RTSP requerem o aplicativo desktop para captura.');
        }
      }
    } catch (err: any) {
      console.error('Capture error:', err);
      setCaptureError(err.message || 'Erro ao capturar foto da câmera');
    } finally {
      setIsCapturing(false);
    }
  }, [selectedIPCamera, ipCameras, buildSnapshotUrl, processImage, isInElectron, cameraPreviewUrl]);

  // Retake photo
  const handleRetake = useCallback(() => {
    setImageSrc(null);
    setCroppedPreview(null);
    setFaceDetectionResult(null);
    setCameraPreviewUrl(null);

    // Restart live preview
    const camera = ipCameras.find(c => c.id === selectedIPCamera);
    if (camera) {
      if (camera.protocol === 'http' || camera.protocol === 'mjpeg') {
        startHttpPreview(camera);
      } else if (camera.protocol === 'rtsp' && isInElectron) {
        startRtspStream(camera);
      }
    }
  }, [ipCameras, selectedIPCamera, startHttpPreview, startRtspStream, isInElectron]);

  const handleConfirm = useCallback(async () => {
    if (!imageSrc || !croppedAreaPixels) return;

    setIsProcessing(true);
    setError(null);

    try {
      const result = await getCroppedImg(
        imageSrc,
        croppedAreaPixels,
        rotation,
        { width: 800, height: 800 },
        { horizontal: false, vertical: false },
        bgColor
      );

      onConfirm({
        blob: result.blob,
        base64: result.base64,
        width: result.width,
        height: result.height,
        faceDetected: faceDetectionResult?.detected || false,
        faceConfidence: faceDetectionResult?.confidence,
        // The full camera snapshot, before padding and cropping.
        original: dataUrlToPhotoOriginal(origDataUrlRef.current),
      });
    } catch (err) {
      console.error('Error processing final image:', err);
      setError('Erro ao processar a imagem. Tente novamente.');
    } finally {
      setIsProcessing(false);
    }
  }, [imageSrc, croppedAreaPixels, rotation, faceDetectionResult, onConfirm, bgColor]);

  const selectedCameraInfo = ipCameras.find(c => c.id === selectedIPCamera);

  // Capture stays here; EDITING is the one shared editor.
  //
  // Everything above this line is capture logic that exists nowhere else: ffmpeg/RTSP through
  // Electron, an MJPEG-via-canvas path for the browser, and the live preview. Replacing this
  // modal with the shared one destroyed exactly that, and IP capture stopped working. Only the
  // captured frame crosses over.
  if (imageSrc) {
    return (
      <PhotoCaptureModalEnhanced
        open={open}
        onClose={onClose}
        onConfirm={onConfirm}
        // The RAW frame, not the padded copy — the editor pads and zoom-cancels it itself.
        // See photoIPCameraHandover: handing over the padded one padded it twice, which is why
        // every capture used to open zoomed out inside a black box.
        existingPhoto={capturedFrameForEditor({ raw: origDataUrlRef.current, padded: imageSrc })!}
        editOnly
        onRetake={handleRetake}
        retakeLabel="Capturar novamente"
        captureSource="ip_camera"
        title={title}
        cropShape={cropShape}
        aspectRatio={aspectRatio}
        disableFaceDetection={disableFaceDetection}
        darkMode={darkMode}
        hideIPCamera
      />
    );
  }


  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      TransitionProps={{
        timeout: {
          enter: 150, // Faster opening
          exit: 150,
        },
      }}
      PaperProps={{
        sx: {
          borderRadius: '12px',
          bgcolor: darkMode ? '#111827' : '#ffffff',
          boxShadow: '0 25px 60px rgba(0, 0, 0, 0.18), 0 8px 24px rgba(0, 0, 0, 0.10)',
          overflow: 'hidden',
          border: darkMode ? '1px solid rgba(255,255,255,0.06)' : 'none',
        }
      }}
    >
      <DialogTitle sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        pb: 1.5,
        pt: 2,
        px: 2.5,
        bgcolor: darkMode ? '#111827' : '#ffffff',
        borderBottom: '1px solid',
        borderColor: darkMode ? 'rgba(255,255,255,0.08)' : '#f0f2f5',
      }}>
        {/* Single row: Title + Camera selector + Controls */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {/* Title */}
          <Typography
            sx={{
              color: darkMode ? '#6b7280' : '#9ca3af',
              fontWeight: 700,
              fontSize: '0.6875rem',
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              whiteSpace: 'nowrap',
              flexShrink: 0,
            }}
          >
            Captura de Imagem em Tempo Real
          </Typography>

          {/* Camera selector — inline, takes remaining space */}
          {!imageSrc && !isLoadingCameras && ipCameras.length > 0 && (
            <FormControl
              size="small"
              sx={{
                flex: 1,
                '& .MuiOutlinedInput-root': {
                  bgcolor: darkMode ? 'rgba(255,255,255,0.04)' : '#f9fafb',
                  borderRadius: '7px',
                  fontSize: '0.8125rem',
                  '& fieldset': {
                    borderColor: darkMode ? 'rgba(255,255,255,0.1)' : '#e5e7eb',
                  },
                  '&:hover fieldset': {
                    borderColor: darkMode ? 'rgba(255,255,255,0.2)' : '#d1d5db',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#6366f1',
                    borderWidth: '1px',
                  },
                },
                '& .MuiInputLabel-root': {
                  color: darkMode ? '#6b7280' : '#9ca3af',
                  fontSize: '0.8125rem',
                  '&.Mui-focused': { color: '#6366f1' },
                },
                '& .MuiSelect-select': {
                  color: darkMode ? '#f9fafb' : '#111827',
                  py: 0.875,
                  fontWeight: 500,
                  fontSize: '0.8125rem',
                },
                '& .MuiSelect-icon': {
                  color: darkMode ? '#6b7280' : '#9ca3af',
                },
              }}
            >
              <InputLabel>Selecione a Câmera</InputLabel>
              <Select
                value={selectedIPCamera}
                label="Selecione a Câmera"
                onChange={(e) => handleCameraChange(e.target.value)}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      bgcolor: darkMode ? '#1f2937' : '#ffffff',
                      border: darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid #e5e7eb',
                      boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                      borderRadius: '8px',
                      '& .MuiMenuItem-root': {
                        color: darkMode ? '#f9fafb' : '#111827',
                        fontSize: '0.8125rem',
                        '&:hover': { bgcolor: darkMode ? 'rgba(255,255,255,0.06)' : '#f9fafb' },
                        '&.Mui-selected': {
                          bgcolor: darkMode ? 'rgba(99,102,241,0.12)' : '#eef2ff',
                          '&:hover': { bgcolor: darkMode ? 'rgba(99,102,241,0.18)' : '#e0e7ff' },
                        },
                      },
                    },
                  },
                }}
              >
                {ipCameras.map((camera) => (
                  <MenuItem key={camera.id} value={camera.id}>
                    {camera.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {/* Controls */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0, ml: 'auto' }}>
            <PhotoHelpTooltip onOpenFullManual={() => setShowGuidelines(true)} />
            <IconButton
              onClick={onClose}
              size="small"
              sx={{
                color: darkMode ? '#6b7280' : '#9ca3af',
                '&:hover': {
                  color: darkMode ? '#d1d5db' : '#374151',
                  bgcolor: darkMode ? 'rgba(255,255,255,0.06)' : '#f3f4f6',
                },
                borderRadius: '6px',
              }}
            >
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>
        </Box>

        {/* Status row: Only reconnection status and errors (no duplicates) */}
        {(isStreamReconnecting || reconnectStatus || streamError) && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            {/* Reconnecting status */}
            {isStreamReconnecting && reconnectStatus && (
              <Chip
                icon={<RefreshIcon sx={{ fontSize: 14, animation: 'spin 2s linear infinite' }} />}
                label={reconnectStatus}
                size="small"
                sx={{
                  height: 26,
                  bgcolor: 'rgba(251, 146, 60, 0.15)',
                  color: '#ea580c',
                  fontWeight: 600,
                  fontSize: '0.75rem',
                  border: '1.5px solid rgba(251, 146, 60, 0.4)',
                  '& .MuiChip-icon': { color: '#ea580c' },
                  '@keyframes spin': {
                    from: { transform: 'rotate(0deg)' },
                    to: { transform: 'rotate(360deg)' },
                  },
                }}
              />
            )}

            {/* Error indicator */}
            {streamError && (
              <Chip
                icon={<WarningIcon sx={{ fontSize: 14 }} />}
                label={streamError}
                size="small"
                sx={{
                  height: 26,
                  bgcolor: 'rgba(239, 68, 68, 0.15)',
                  color: '#dc2626',
                  fontWeight: 600,
                  fontSize: '0.75rem',
                  border: '1.5px solid rgba(239, 68, 68, 0.4)',
                  '& .MuiChip-icon': { color: '#dc2626' },
                }}
                onDelete={() => setStreamError(null)}
              />
            )}
          </Box>
        )}
      </DialogTitle>

      <DialogContent sx={{ bgcolor: darkMode ? '#111827' : '#f9fafb', p: 0 }}>
        {(error || captureError) && (
          <Box sx={{
            mx: 2.5, mt: 2, px: 3, py: 2,
            bgcolor: darkMode ? 'rgba(239,68,68,0.08)' : '#fef2f2',
            border: '1px solid',
            borderColor: darkMode ? 'rgba(239,68,68,0.2)' : '#fecaca',
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
          }}>
            <Typography sx={{ fontSize: '0.8125rem', color: '#dc2626', fontWeight: 500 }}>
              {error || captureError}
            </Typography>
            <IconButton size="small" onClick={() => { setError(null); setCaptureError(null); }} sx={{ color: '#dc2626', p: 0.5 }}>
              <CloseIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Box>
        )}

        {!imageSrc ? (
          // Camera selection and capture
          <Box>
            {isLoadingCameras ? (
              <Box sx={{ textAlign: 'center', py: 6, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
                <CircularProgress size={28} sx={{ color: '#6366f1' }} />
                <Typography sx={{ fontSize: '0.875rem', color: darkMode ? '#6b7280' : '#9ca3af' }}>Carregando câmeras...</Typography>
              </Box>
            ) : ipCameras.length === 0 ? (
              <Box sx={{ px: 3, py: 6, textAlign: 'center' }}>
                <Box sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 48,
                  height: 48,
                  borderRadius: '10px',
                  bgcolor: darkMode ? 'rgba(99,102,241,0.12)' : '#eef2ff',
                  border: '1px solid',
                  borderColor: darkMode ? 'rgba(99,102,241,0.2)' : '#c7d2fe',
                  mb: 2,
                }}>
                  <IPCameraIcon sx={{ fontSize: 24, color: darkMode ? '#818cf8' : '#4f46e5' }} />
                </Box>
                <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: darkMode ? '#f9fafb' : '#111827', mb: 0.5 }}>
                  Nenhuma câmera cadastrada
                </Typography>
                <Typography sx={{ fontSize: '0.8125rem', color: darkMode ? '#6b7280' : '#9ca3af' }}>
                  {filterCategory
                    ? `Configure câmeras na categoria "${filterCategory}" na seção de dispositivos.`
                    : 'Configure câmeras IP na seção de dispositivos.'}
                </Typography>
              </Box>
            ) : (
              <>
                {/* Live preview area */}
                <Box
                  sx={{
                    height: 400,
                    bgcolor: '#0f172a',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexDirection: 'column',
                    overflow: 'hidden',
                    position: 'relative',
                  }}
                >
                  {cameraPreviewUrl ? (
                    // Camera preview (works for both HTTP and RTSP)
                    <>
                      <img
                        key={cameraPreviewUrl}
                        src={cameraPreviewUrl}
                        alt="Camera preview"
                        style={{
                          maxWidth: '100%',
                          maxHeight: '100%',
                          objectFit: 'contain',
                        }}
                        onError={() => {
                          console.error('Camera preview failed to load');
                        }}
                      />
                      {/* Bottom Control Bar - YouTube/Twitch style */}
                      <Box
                        sx={{
                          position: 'absolute',
                          bottom: 0,
                          left: 0,
                          right: 0,
                          background: 'linear-gradient(to top, rgba(0, 0, 0, 0.85) 0%, rgba(0, 0, 0, 0.6) 70%, transparent 100%)',
                          backdropFilter: 'blur(8px)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          px: 2,
                          py: 1.5,
                          gap: 2,
                        }}
                      >
                        {/* Left side - Status indicators */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          {/* When streaming: Show "AO VIVO" badge */}
                          {/* When not streaming: Show Online/Offline status */}
                          {(isStreaming || httpSnapshotUrl) ? (
                            <>
                              {/* AO VIVO badge - replaces online/offline when streaming */}
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 0.5,
                                  background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                                  px: 1.25,
                                  py: 0.5,
                                  borderRadius: 2,
                                  boxShadow: '0 0 12px rgba(239, 68, 68, 0.5)',
                                }}
                              >
                                <LiveIcon sx={{ fontSize: 8, color: 'white', animation: 'pulse 1.5s ease-in-out infinite' }} />
                                <Typography
                                  sx={{
                                    fontSize: '0.7rem',
                                    fontWeight: 'bold',
                                    color: 'white',
                                    letterSpacing: '0.05em',
                                  }}
                                >
                                  AO VIVO
                                </Typography>
                              </Box>

                              {/* FPS indicator - only show when streaming */}
                              {actualFps > 0 && (
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    bgcolor: 'rgba(255, 255, 255, 0.1)',
                                    backdropFilter: 'blur(4px)',
                                    px: 1.25,
                                    py: 0.5,
                                    borderRadius: 2,
                                    border: '1px solid rgba(255, 255, 255, 0.15)',
                                  }}
                                >
                                  <Typography
                                    sx={{
                                      fontSize: '0.7rem',
                                      fontWeight: 600,
                                      color: 'rgba(255, 255, 255, 0.95)',
                                    }}
                                  >
                                    {actualFps} FPS
                                  </Typography>
                                </Box>
                              )}
                            </>
                          ) : null}
                        </Box>

                        {/* Right side - Controls */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {/* Quality Selector - only show for RTSP cameras */}
                          {selectedIPCamera && ipCameras.find(c => c.id === selectedIPCamera)?.protocol === 'rtsp' && (
                            <Box sx={{ position: 'relative' }}>
                              <IconButton
                                size="small"
                                onClick={() => setShowQualityMenu(!showQualityMenu)}
                                sx={{
                                  bgcolor: showQualityMenu ? 'rgba(255, 255, 255, 0.2)' : 'rgba(255, 255, 255, 0.1)',
                                  backdropFilter: 'blur(4px)',
                                  color: 'white',
                                  border: '1px solid rgba(255, 255, 255, 0.15)',
                                  px: 1.5,
                                  py: 0.5,
                                  borderRadius: 2,
                                  '&:hover': {
                                    bgcolor: 'rgba(255, 255, 255, 0.25)',
                                    borderColor: 'rgba(255, 255, 255, 0.3)',
                                  },
                                  transition: 'all 0.2s',
                                }}
                              >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                  <SettingsIcon sx={{ fontSize: 16 }} />
                                  <Typography sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
                                    {streamQuality === 'low' && '480p'}
                                    {streamQuality === 'medium' && '720p'}
                                    {streamQuality === 'high' && '1080p'}
                                    {streamQuality === 'ultra' && '1080p+'}
                                  </Typography>
                                </Box>
                              </IconButton>

                              {/* Quality Menu Popup */}
                              {showQualityMenu && (
                                <Box
                                  sx={{
                                    position: 'absolute',
                                    bottom: '100%',
                                    right: 0,
                                    mb: 1,
                                    bgcolor: 'rgba(20, 20, 20, 0.98)',
                                    backdropFilter: 'blur(12px)',
                                    borderRadius: 2,
                                    border: '1px solid rgba(255, 255, 255, 0.15)',
                                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5)',
                                    overflow: 'hidden',
                                    minWidth: 240,
                                    zIndex: 10,
                                  }}
                                >
                                  {/* Quality options */}
                                  {[
                                    { value: 'low', label: '480p', desc: 'Economia de Dados', fps: '15 FPS' },
                                    { value: 'medium', label: '720p', desc: 'Recomendado', fps: '25 FPS', recommended: true },
                                    { value: 'high', label: '1080p', desc: 'Alta Qualidade', fps: '25 FPS' },
                                    { value: 'ultra', label: '1080p+', desc: 'Qualidade Máxima', fps: '30 FPS' },
                                  ].map((option) => (
                                    <Box
                                      key={option.value}
                                      onClick={() => {
                                        const newQuality = option.value as 'low' | 'medium' | 'high' | 'ultra';
                                        setStreamQuality(newQuality);
                                        localStorage.setItem('rtsp-stream-quality', newQuality);
                                        setShowQualityMenu(false);
                                        // Restart stream with new quality if currently streaming
                                        if (isStreaming) {
                                          const camera = ipCameras.find(c => c.id === selectedIPCamera);
                                          if (camera) {
                                            stopCameraPreview().then(() => {
                                              setTimeout(() => startRtspStream(camera), 200);
                                            });
                                          }
                                        }
                                      }}
                                      sx={{
                                        px: 2,
                                        py: 1.5,
                                        cursor: 'pointer',
                                        bgcolor: streamQuality === option.value ? 'rgba(37, 99, 235, 0.2)' : 'transparent',
                                        borderLeft: streamQuality === option.value ? '3px solid #2563eb' : '3px solid transparent',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        gap: 2,
                                        '&:hover': {
                                          bgcolor: 'rgba(255, 255, 255, 0.08)',
                                        },
                                        transition: 'all 0.15s',
                                      }}
                                    >
                                      <Box sx={{ flex: 1 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.25 }}>
                                          <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: 'white' }}>
                                            {option.label}
                                          </Typography>
                                          {option.recommended && (
                                            <Chip
                                              label=""
                                              size="small"
                                              sx={{
                                                height: 16,
                                                fontSize: '0.65rem',
                                                bgcolor: '#f59e0b',
                                                color: 'white',
                                                '& .MuiChip-label': { px: 0.75 },
                                              }}
                                            />
                                          )}
                                        </Box>
                                        <Typography sx={{ fontSize: '0.7rem', color: 'rgba(255, 255, 255, 0.6)' }}>
                                          {option.desc} • {option.fps}
                                        </Typography>
                                      </Box>
                                      {streamQuality === option.value && (
                                        <CheckmarkIcon sx={{ fontSize: 18, color: '#2563eb' }} />
                                      )}
                                    </Box>
                                  ))}
                                </Box>
                              )}
                            </Box>
                          )}

                          {/* Refresh button */}
                          <IconButton
                            size="small"
                            onClick={async () => {
                              const camera = ipCameras.find(c => c.id === selectedIPCamera);
                              if (camera) {
                                await stopCameraPreview();
                                setTimeout(() => {
                                  if (camera.protocol === 'rtsp') {
                                    startRtspStream(camera);
                                  } else if (camera.protocol === 'http' || camera.protocol === 'mjpeg') {
                                    startHttpPreview(camera);
                                  }
                                }, 200);
                              }
                            }}
                            sx={{
                              bgcolor: 'rgba(255, 255, 255, 0.1)',
                              backdropFilter: 'blur(4px)',
                              color: 'white',
                              border: '1px solid rgba(255, 255, 255, 0.15)',
                              '&:hover': {
                                bgcolor: 'rgba(255, 255, 255, 0.2)',
                                borderColor: 'rgba(255, 255, 255, 0.3)',
                                transform: 'rotate(90deg)',
                              },
                              transition: 'all 0.3s',
                            }}
                          >
                            <RefreshIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </Box>
                    </>
                  ) : httpSnapshotUrl ? (
                    // Snapshot polling fallback (Digest auth via Electron — ffmpeg MJPEG unavailable)
                    <>
                      <img
                        src={httpSnapshotUrl}
                        alt="Camera preview"
                        style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                      />
                      {/* AO VIVO badge */}
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 12,
                          left: 12,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                          background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                          px: 1.25,
                          py: 0.5,
                          borderRadius: 2,
                          boxShadow: '0 0 12px rgba(239, 68, 68, 0.5)',
                        }}
                      >
                        <LiveIcon sx={{ fontSize: 8, color: 'white', animation: 'pulse 1.5s ease-in-out infinite' }} />
                        <Typography sx={{ fontSize: '0.7rem', fontWeight: 'bold', color: 'white', letterSpacing: '0.05em' }}>
                          AO VIVO
                        </Typography>
                      </Box>
                    </>
                  ) : isStreaming ? (
                    // Stream is starting but no frames yet
                    <>
                      <IPCameraIcon sx={{ fontSize: 64, color: 'rgba(255, 255, 255, 0.3)', mb: 2 }} />
                      <Typography sx={{ color: 'rgba(255, 255, 255, 0.7)' }} textAlign="center">
                        Conectando ao stream...
                      </Typography>
                      <CircularProgress size={24} sx={{ mt: 1, color: '#64748b' }} />

                      {/* Bottom Control Bar - Connecting state */}
                      <Box
                        sx={{
                          position: 'absolute',
                          bottom: 0,
                          left: 0,
                          right: 0,
                          background: 'linear-gradient(to top, rgba(0, 0, 0, 0.85) 0%, rgba(0, 0, 0, 0.6) 70%, transparent 100%)',
                          backdropFilter: 'blur(8px)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          px: 2,
                          py: 1.5,
                        }}
                      >
                        {/* Left side - Connecting status */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.75,
                              bgcolor: 'rgba(251, 191, 36, 0.2)',
                              backdropFilter: 'blur(4px)',
                              px: 1.25,
                              py: 0.5,
                              borderRadius: 2,
                              border: '1px solid rgba(251, 191, 36, 0.4)',
                            }}
                          >
                            <CircularProgress size={10} sx={{ color: '#fbbf24' }} />
                            <Typography
                              sx={{
                                fontSize: '0.7rem',
                                fontWeight: 600,
                                color: '#fbbf24',
                                letterSpacing: '0.03em',
                                textTransform: 'uppercase',
                              }}
                            >
                              Conectando
                            </Typography>
                          </Box>
                        </Box>

                        {/* Right side - Refresh button */}
                        <IconButton
                          size="small"
                          onClick={async () => {
                            const camera = ipCameras.find(c => c.id === selectedIPCamera);
                            if (camera) {
                              await stopCameraPreview();
                              setTimeout(() => {
                                if (camera.protocol === 'rtsp') {
                                  startRtspStream(camera);
                                } else if (camera.protocol === 'http' || camera.protocol === 'mjpeg') {
                                  startHttpPreview(camera);
                                }
                              }, 200);
                            }
                          }}
                          sx={{
                            bgcolor: 'rgba(239, 68, 68, 0.2)',
                            backdropFilter: 'blur(4px)',
                            color: '#ef4444',
                            border: '1px solid rgba(239, 68, 68, 0.4)',
                            '&:hover': {
                              bgcolor: 'rgba(239, 68, 68, 0.3)',
                              borderColor: 'rgba(239, 68, 68, 0.6)',
                              transform: 'rotate(90deg)',
                            },
                            transition: 'all 0.3s',
                          }}
                        >
                          <RefreshIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </>
                  ) : selectedCameraInfo?.protocol === 'rtsp' && !isInElectron ? (
                    // RTSP requires Electron
                    <>
                      <IPCameraIcon sx={{ fontSize: 64, color: 'rgba(255, 255, 255, 0.3)', mb: 2 }} />
                      <Typography sx={{ color: '#facc15' }} textAlign="center">
                        Câmeras RTSP requerem o aplicativo desktop
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)', mt: 1 }} textAlign="center">
                        Use o aplicativo Electron para capturar fotos de câmeras RTSP
                      </Typography>
                    </>
                  ) : selectedIPCamera ? (
                    // Camera selected but stream not active — connecting
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
                      <IPCameraIcon sx={{ fontSize: 48, color: 'rgba(255,255,255,0.25)', mb: 1 }} />
                      <CircularProgress size={28} sx={{ color: '#64748b' }} />
                      <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.875rem' }}>
                        Conectando...
                      </Typography>
                    </Box>
                  ) : (
                    // No camera selected
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
                      <Box sx={{
                        width: 56,
                        height: 56,
                        borderRadius: '12px',
                        bgcolor: 'rgba(99,102,241,0.12)',
                        border: '1px solid rgba(99,102,241,0.2)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}>
                        <IPCameraIcon sx={{ fontSize: 28, color: '#818cf8' }} />
                      </Box>
                      <Typography sx={{ color: 'rgba(255,255,255,0.55)', fontSize: '0.875rem', fontWeight: 500 }}>
                        Selecione uma câmera para iniciar
                      </Typography>
                      <Typography sx={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.75rem' }}>
                        Escolha uma câmera no seletor acima
                      </Typography>
                    </Box>
                  )}

                  {isCapturing && (
                    <Box
                      sx={{
                        position: 'absolute',
                        inset: 0,
                        bgcolor: 'rgba(15, 23, 42, 0.9)',
                        backdropFilter: 'blur(4px)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexDirection: 'column',
                      }}
                    >
                      <CircularProgress sx={{ color: '#64748b' }} />
                      <Typography sx={{ mt: 2, color: 'white', fontWeight: 500 }}>Capturando...</Typography>
                    </Box>
                  )}

                  {/* Shutter. Only once a frame has actually arrived: it used to sit over
                      "Conectando ao stream…" inviting a press that could only fail. */}
                  {canCaptureFrame({ previewUrl: cameraPreviewUrl, imageSrc, isCapturing }) && (
                    <Box
                      sx={{
                        position: 'absolute',
                        bottom: 24,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 1,
                        zIndex: 20,
                      }}
                    >
                      {/* A shutter, not a toolbar button: a thin ring with the brand fill
                          inside it, which stays legible over any frame and does not compete
                          with the video for attention. */}
                      <IconButton
                        onClick={handleCapture}
                        disabled={!selectedIPCamera}
                        aria-label="Capturar foto"
                        sx={{
                          width: 72,
                          height: 72,
                          p: 0,
                          bgcolor: 'transparent',
                          border: '3px solid rgba(255, 255, 255, 0.9)',
                          boxShadow: '0 2px 12px rgba(0, 0, 0, 0.45)',
                          '&:hover': {
                            bgcolor: 'transparent',
                            borderColor: '#ffffff',
                            '& .shutter-core': { transform: 'scale(1.06)' },
                          },
                          '&:active .shutter-core': { transform: 'scale(0.92)' },
                          '&:disabled': { borderColor: 'rgba(255, 255, 255, 0.35)' },
                          transition: 'border-color 0.15s ease',
                        }}
                      >
                        <Box
                          className="shutter-core"
                          sx={{
                            width: 56,
                            height: 56,
                            borderRadius: '50%',
                            bgcolor: selectedIPCamera ? '#2563eb' : 'rgba(148, 163, 184, 0.6)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'transform 0.15s ease, background-color 0.15s ease',
                          }}
                        >
                          <CaptureIcon sx={{ fontSize: 26, color: 'white' }} />
                        </Box>
                      </IconButton>
                    </Box>
                  )}
                </Box>
              </>
            )}
          </Box>
        ) : (
          // Cropper area
          <Box sx={{ display: 'flex', gap: 3, p: 2.5, bgcolor: darkMode ? '#111827' : '#ffffff' }}>
            {/* Left: Cropper */}
            <Box sx={{ flex: 1 }}>
              <Box
                sx={{
                  position: 'relative',
                  height: 400,
                  bgcolor: '#0f172a',
                  borderRadius: 1,
                  overflow: 'hidden',
                }}
              >
                {isDetectingFace ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                    <CircularProgress sx={{ color: '#64748b' }} />
                    <Typography sx={{ ml: 2, color: 'rgba(255, 255, 255, 0.8)' }}>Detectando rosto...</Typography>
                  </Box>
                ) : (
                  <Cropper
                    key={imageSrc} // Force remount when image changes to prevent DOM errors
                    image={imageSrc}
                    crop={crop}
                    zoom={zoom}
                    rotation={rotation}
                    aspect={aspectRatio}
                    cropShape={cropShape}
                    showGrid={cropShape === 'rect'}
                    onCropChange={setCrop}
                    onCropComplete={onCropComplete}
                    onZoomChange={setZoom}
                    // The library default of 1 makes the wheel crawl: a whole photo down to a
                    // number plate is one long unbroken scroll.
                    zoomSpeed={2.5}
                    minZoom={0.1}
                    maxZoom={CROPPER_MAX_ZOOM}
                    restrictPosition={false}
                  />
                )}
              </Box>

              {/* Controls */}
              <Box sx={{ mt: 2, px: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                  <ZoomInIcon fontSize="small" sx={{ color: '#64748b' }} />
                  <Slider
                    value={zoom}
                    min={0.1}
                    max={CROPPER_MAX_ZOOM}
                    step={0.1}
                    onChange={(_, value) => setZoom(value as number)}
                    size="small"
                    sx={{
                      flex: 1,
                      color: '#2563eb',
                      '& .MuiSlider-track': {
                        bgcolor: '#2563eb',
                      },
                      '& .MuiSlider-thumb': {
                        bgcolor: 'white',
                        border: '2px solid #2563eb',
                        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                      },
                      '& .MuiSlider-rail': {
                        bgcolor: 'rgba(0, 0, 0, 0.1)',
                      },
                    }}
                  />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <RotateIcon fontSize="small" sx={{ color: '#64748b' }} />
                  <Slider
                    value={rotation}
                    min={0}
                    max={360}
                    onChange={(_, value) => setRotation(value as number)}
                    size="small"
                    sx={{
                      flex: 1,
                      color: '#2563eb',
                      '& .MuiSlider-track': {
                        bgcolor: '#2563eb',
                      },
                      '& .MuiSlider-thumb': {
                        bgcolor: 'white',
                        border: '2px solid #2563eb',
                        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                      },
                      '& .MuiSlider-rail': {
                        bgcolor: 'rgba(0, 0, 0, 0.1)',
                      },
                    }}
                  />
                </Box>
                {/* Background color toggle */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.5 }}>
                  <Typography variant="caption" sx={{ color: '#64748b' }}>Fundo:</Typography>
                  <IconButton
                    size="small"
                    onClick={() => setBgColor('#000000')}
                    title="Fundo preto"
                    sx={{
                      width: 22, height: 22, bgcolor: '#000000', borderRadius: 0.5,
                      border: bgColor === '#000000' ? '2px solid #2563eb' : '2px solid #999',
                      '&:hover': { bgcolor: '#222' },
                    }}
                  />
                  <IconButton
                    size="small"
                    onClick={() => setBgColor('#ffffff')}
                    title="Fundo branco"
                    sx={{
                      width: 22, height: 22, bgcolor: '#ffffff', borderRadius: 0.5,
                      border: bgColor === '#ffffff' ? '2px solid #2563eb' : '2px solid #999',
                      '&:hover': { bgcolor: '#eee' },
                    }}
                  />
                </Box>
              </Box>
            </Box>

            {/* Right: Preview */}
            <Box sx={{ width: 200, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <Typography variant="subtitle2" sx={{ color: '#64748b', mb: 1.5, fontSize: '0.8rem' }}>
                Prévia
              </Typography>
              <Box
                sx={{
                  width: cropShape === 'rect' ? 200 : 160,
                  height: cropShape === 'rect' ? Math.round(200 / aspectRatio) : 160,
                  borderRadius: cropShape === 'rect' ? 1 : '50%',
                  overflow: 'hidden',
                  border: '2px solid',
                  borderColor: faceDetectionResult?.detected ? '#22c55e' : 'rgba(0, 0, 0, 0.15)',
                  bgcolor: '#f1f5f9',
                  boxShadow: faceDetectionResult?.detected
                    ? '0 0 12px rgba(34, 197, 94, 0.3)'
                    : '0 2px 8px rgba(0, 0, 0, 0.08)',
                }}
              >
                {croppedPreview && (
                  <img
                    src={croppedPreview}
                    alt="Preview"
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                )}
              </Box>

              {/* Face detection status */}
              <Box sx={{ mt: 1.5, textAlign: 'center' }}>
                {faceDetectionResult?.detected ? (
                  <Box
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      bgcolor: 'rgba(34, 197, 94, 0.1)',
                      border: '1px solid rgba(34, 197, 94, 0.25)',
                      borderRadius: 1,
                      px: 1,
                      py: 0.5,
                    }}
                  >
                    <CheckIcon sx={{ fontSize: 16, mr: 0.5, color: '#16a34a' }} />
                    <Typography variant="caption" sx={{ color: '#16a34a', fontSize: '0.7rem' }}>Rosto detectado</Typography>
                  </Box>
                ) : faceDetectionResult && !faceDetectionResult.detected ? (
                  <Box
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      bgcolor: 'rgba(234, 179, 8, 0.1)',
                      border: '1px solid rgba(234, 179, 8, 0.25)',
                      borderRadius: 1,
                      px: 1,
                      py: 0.5,
                    }}
                  >
                    <WarningIcon sx={{ fontSize: 16, mr: 0.5, color: '#ca8a04' }} />
                    <Typography variant="caption" sx={{ color: '#ca8a04', fontSize: '0.7rem' }}>Rosto não detectado</Typography>
                  </Box>
                ) : null}
              </Box>
            </Box>
          </Box>
        )}
      </DialogContent>

      {/* Bottom Actions - Only show when editing/cropping */}
      {imageSrc && (
        <DialogActions
          sx={{
            px: 2.5,
            py: 2,
            gap: 1.5,
            bgcolor: darkMode ? '#111827' : '#ffffff',
            borderTop: '1px solid',
            borderColor: darkMode ? 'rgba(255,255,255,0.08)' : '#f0f2f5',
          }}
        >
          <Button
            onClick={handleRetake}
            size="medium"
            sx={{
              color: darkMode ? '#9ca3af' : '#6b7280',
              borderRadius: '8px',
              textTransform: 'none',
              px: 2.5,
              py: 1,
              fontSize: '0.875rem',
              fontWeight: 500,
              border: '1px solid',
              borderColor: darkMode ? 'rgba(255,255,255,0.1)' : '#e5e7eb',
              '&:hover': {
                bgcolor: darkMode ? 'rgba(255,255,255,0.05)' : '#f9fafb',
                borderColor: darkMode ? 'rgba(255,255,255,0.2)' : '#d1d5db',
              },
            }}
          >
            Tirar Novamente
          </Button>

          <Button
            variant="contained"
            onClick={handleConfirm}
            disabled={!croppedAreaPixels || isProcessing}
            startIcon={isProcessing ? <CircularProgress size={16} sx={{ color: 'white' }} /> : <CheckIcon sx={{ fontSize: 16 }} />}
            size="medium"
            sx={{
              px: 2.5,
              py: 1,
              borderRadius: '8px',
              bgcolor: '#4f46e5',
              textTransform: 'none',
              fontSize: '0.875rem',
              fontWeight: 600,
              boxShadow: 'none',
              '&:hover': {
                bgcolor: '#4338ca',
                boxShadow: '0 4px 12px rgba(79,70,229,0.3)',
              },
              '&:disabled': {
                bgcolor: darkMode ? 'rgba(255,255,255,0.08)' : '#e5e7eb',
                color: darkMode ? '#4b5563' : '#9ca3af',
              },
            }}
          >
            {isProcessing ? 'Processando...' : 'Usar Foto'}
          </Button>
        </DialogActions>
      )}

      {/* Photo Guidelines Modal */}
      <PhotoGuidelinesModal
        open={showGuidelines}
        onClose={() => setShowGuidelines(false)}
      />
    </Dialog>
  );
};

export default PhotoIPCameraModal;
